import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { searchCompanyDetailsService } from '../../Api/ContactApi';
import { Ripple } from 'primereact/ripple';
import { CompanyInterface } from '../../Controllers/Types/CompanyType';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { checkEmail, checkText } from '../../Utils/Validator';

interface CompanySearchDialogProps {
    visible: boolean;
    onHide: () => void;
    onExistingCompanyDetailsConfirm: (companyDetails: CompanyInterface | null) => void;
    onCompanyDetailsConfirm: (companyDetails: CompanyInterface | null) => void;
}

const CompanySearchDialog: React.FC<CompanySearchDialogProps> = ({
    visible,
    onHide,
    onExistingCompanyDetailsConfirm,
    onCompanyDetailsConfirm,
}) => {
    const [searchCompanyName, setSearchCompanyName] = useState<string>('');
    const [searchCompanyRegNo, setSearchCompanyRegNo] = useState<string>('');
    const [searchCompanyEmail, setSearchCompanyEmail] = useState<string>('');
    const [errorSearchCompanyDetails, setErrorSearchCompanyDetails] = useState<string>('');
    const [loadingSearchCompanyDetails, setLoadingSearchCompanyDetails] = useState<boolean>(false);
    const [isExistsCompanyDetails, setIsExistsCompanyDetails] = useState<boolean | null>(null);
    const [companyDetails, setCompanyDetails] = useState<CompanyInterface | null>(null);

    const handleSearchCompanyDetails = async () => {
        if (checkSearchCompanyDetailsCredentials()) {
            setErrorSearchCompanyDetails('')
            setLoadingSearchCompanyDetails(true);

            const response = await searchCompanyDetailsService(searchCompanyName, searchCompanyRegNo, searchCompanyEmail);
            setLoadingSearchCompanyDetails(false);

            if (response.status) {
                const _isExistsCompanyDetails = response.data['have_data']
                const companyDetails = response.data['data']

                if (_isExistsCompanyDetails) {
                    const company: CompanyInterface = {
                        company_name: companyDetails['company_name'] || '',
                        company_regno: companyDetails['company_regno'] || '',
                        company_email: companyDetails['company_email'] || '',
                        company_phone: companyDetails['company_phone'] || '',
                        company_address: companyDetails['company_address'] || '',
                        city: companyDetails['city'] || '',
                        post_code: companyDetails['post_code'] || '',
                    }
                    setCompanyDetails(company)
                } else {
                    const company: CompanyInterface = {
                        company_name: searchCompanyName || '',
                        company_regno: searchCompanyRegNo || '',
                        company_email: searchCompanyEmail || '',
                        company_phone: '',
                        company_address: '',
                        city: '',
                        post_code: '',
                    }
                    setCompanyDetails(company)
                }
                setIsExistsCompanyDetails(_isExistsCompanyDetails);
            }

            setTimeout(() => {
                const dialogContent = document.querySelector('.p-dialog-content') as HTMLDivElement;
                if (dialogContent) {
                    dialogContent.scrollTop = dialogContent.scrollHeight;
                }
            }, 100);
        }
    }

    function checkSearchCompanyDetailsCredentials(): boolean {
        if (searchCompanyName.length > 0 || searchCompanyRegNo.length > 0 || searchCompanyEmail.length > 0) {
            if (searchCompanyEmail.length > 0) {
                const isSearchEmailValid = checkText(searchCompanyEmail, true, setErrorSearchCompanyDetails) && checkEmail(searchCompanyEmail, false, setErrorSearchCompanyDetails);

                return isSearchEmailValid;
            } else {
                return true;
            }
        } else {
            setErrorSearchCompanyDetails('Please enter company name or Reg.No or Email address.')
            return false;
        }
    }

    const handleUseThisExistingCompanyDetails = () => {
        onExistingCompanyDetailsConfirm(companyDetails);
        resetDialog();
    };

    const handleUseThisCompanyDetails = () => {
        onCompanyDetailsConfirm(companyDetails);
        resetDialog();
    };

    const handleOnHide = () => {
        onHide();
        setCompanyDetails(null);
        setSearchCompanyRegNo('');
        setSearchCompanyName('');
        setErrorSearchCompanyDetails('');
        setLoadingSearchCompanyDetails(false);
        setIsExistsCompanyDetails(null);
    };

    const resetDialog = () => {
        setCompanyDetails(null);
        setSearchCompanyRegNo('');
        setSearchCompanyName('');
        setErrorSearchCompanyDetails('');
        setLoadingSearchCompanyDetails(false);
        setIsExistsCompanyDetails(null);
        onHide();
    };

    const addSearchEmailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Email validity
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleOnHide}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            header={addSearchEmailModalHeader}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={resetDialog}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                    <div className="row">
                        <div className="col-12">
                            <div className="page_form_group">
                                <Message
                                    severity='info'
                                    className="message_text w-100"
                                    icon="bi bi-info-circle"
                                    style={{ border: '1px solid', borderRadius: '8px' }}
                                    text={`Please enter the company details. This will help you find a company that is already a Traqdo member. To find the company you can enter the company name or company reg.no. or company email address.`}
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="page_form_group mb-3">
                                <label htmlFor="companyName" className="custom_form_label">Company name</label>
                                <div className="input_flex_group">
                                    <InputText
                                        id="companyName"
                                        value={searchCompanyName}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setSearchCompanyName(e.target.value);
                                            setIsExistsCompanyDetails(null);
                                            setCompanyDetails(null);
                                            setErrorSearchCompanyDetails('');
                                        }}
                                        className="custom_form_input"
                                        placeholder="Company name"
                                        type='search'
                                        autoComplete='off'
                                    />

                                    <Button
                                        loading={(searchCompanyName && loadingSearchCompanyDetails) ? true : false}
                                        icon="bi bi-search"
                                        className='search_input_btn'
                                        onClick={handleSearchCompanyDetails}
                                    />
                                </div>
                            </div>

                            <div className="page_form_group mb-3">
                                <label htmlFor="companyRegNo" className="custom_form_label">Reg.No.</label>
                                <div className="input_flex_group">
                                    <InputText
                                        id="companyRegNo"
                                        value={searchCompanyRegNo}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setSearchCompanyRegNo(e.target.value);
                                            setIsExistsCompanyDetails(null);
                                            setCompanyDetails(null);
                                            setErrorSearchCompanyDetails('');
                                        }}
                                        className="custom_form_input"
                                        placeholder="Reg.No"
                                        type='search'
                                        autoComplete='off'
                                    />

                                    <Button
                                        loading={(searchCompanyRegNo && loadingSearchCompanyDetails) ? true : false}
                                        icon="bi bi-search"
                                        className='search_input_btn'
                                        onClick={handleSearchCompanyDetails}
                                    />
                                </div>
                            </div>

                            <div className="page_form_group mb-4">
                                <label htmlFor="companyEmail" className="custom_form_label">Email</label>
                                <div className="input_flex_group">
                                    <InputText
                                        id="companyEmail"
                                        value={searchCompanyEmail}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setSearchCompanyEmail(e.target.value);
                                            setIsExistsCompanyDetails(null);
                                            setCompanyDetails(null);
                                            setErrorSearchCompanyDetails('');
                                        }}
                                        className="custom_form_input"
                                        placeholder="Email"
                                        type='search'
                                        autoComplete='off'
                                    />

                                    <Button
                                        loading={(searchCompanyEmail && loadingSearchCompanyDetails) ? true : false}
                                        icon="bi bi-search"
                                        className='search_input_btn'
                                        onClick={handleSearchCompanyDetails}
                                    />
                                </div>
                            </div>

                            <div className="page_form_group">
                                {errorSearchCompanyDetails &&
                                    <Message
                                        severity='error'
                                        className="message_text w-100"
                                        icon="bi bi-exclamation-circle"
                                        style={{ border: '1px solid', borderRadius: '8px' }}
                                        text={errorSearchCompanyDetails}
                                    />
                                }
                            </div>
                        </div>

                        {(isExistsCompanyDetails != null && isExistsCompanyDetails) ? (
                            <div className="col-12">
                                <div className="page_form_group mb-3">
                                    <Message
                                        severity='warn'
                                        className="message_text w-100"
                                        icon="bi bi-exclamation-circle"
                                        style={{ border: '1px solid', borderRadius: '8px' }}
                                        text="This company is already a member of Traqdo."
                                    />
                                </div>

                                {companyDetails != null && (
                                    <div className="page_form_group">
                                        <label htmlFor="companyDetails" className="custom_form_label text_purple mb-2"
                                            style={{ opacity: '1' }}>
                                            <i className='bi bi-buildings-fill me-1'></i>
                                            Company details :
                                        </label>
                                        <article className="payment_summary_area" id='companyDetails'>
                                            <div className="payment_summary_info">
                                                <h6>Name :</h6>
                                                <p className='text-capitalize'>
                                                    {companyDetails.company_name ? companyDetails.company_name : '----'}
                                                </p>
                                            </div>

                                            <div className="payment_summary_info">
                                                <h6>Email :</h6>
                                                <p>
                                                    {companyDetails.company_email ? companyDetails.company_email : '----'}
                                                </p>
                                            </div>

                                            <div className="payment_summary_info">
                                                <h6>Reg.No. :</h6>
                                                <p className='text-capitalize'>
                                                    {companyDetails.company_regno ? companyDetails.company_regno : '----'}
                                                </p>
                                            </div>

                                            <div className="payment_summary_info">
                                                <h6>Contact.No. :</h6>
                                                <p className='text-capitalize'>
                                                    {companyDetails.company_phone ? companyDetails.company_phone : '----'}
                                                </p>
                                            </div>

                                            <div className="payment_summary_info">
                                                <h6>Address :</h6>
                                                <p className='text-capitalize'>
                                                    {companyDetails.company_address ? companyDetails.company_address : '----'}
                                                </p>
                                            </div>
                                        </article>
                                    </div>
                                )}

                                <hr className='my-4' />

                                <Button
                                    label='Use this company details'
                                    icon="bi bi-check-circle"
                                    onClick={handleUseThisExistingCompanyDetails}
                                    className='float-end'
                                />
                            </div>
                        ) : (isExistsCompanyDetails != null && !isExistsCompanyDetails) ? (
                            <React.Fragment>
                                {(searchCompanyName !== '' || searchCompanyEmail !== '' || searchCompanyRegNo !== '') && (
                                    <div className="col-12">
                                        <Message
                                            severity='success'
                                            className="message_text w-100"
                                            icon="bi bi-check-circle"
                                            style={{ border: '1px solid', borderRadius: '8px' }}
                                            text={`This company details is not associated with a Traqdo member. You can use this details as your contact.`}
                                        />
                                        <hr className='my-4' />
                                        <Button
                                            label='Use this details'
                                            icon="bi bi-check-circle"
                                            onClick={handleUseThisCompanyDetails}
                                            className='float-end'
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        ) : null}
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default CompanySearchDialog;
